import { TStoryContentElementTypeCustomEmbed } from '@dl/declarations';
import { BlockWithAlignableContents } from "@lexical/react/LexicalBlockWithAlignableContents";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { DecoratorBlockNode, SerializedDecoratorBlockNode } from "@lexical/react/LexicalDecoratorBlockNode";
import { Breakpoint } from '@mui/material';
import clsx from 'clsx';
import { $getNodeByKey, DOMConversionOutput, ElementFormatType, NodeKey, Spread } from "lexical";
import React, { JSX } from "react";

import { ControlledSystemDialog } from '@cms/components/data/Dialog';
import { SystemTooltip } from "../../ui/Tooltip";

import { FaRegTrashCan } from 'react-icons/fa6';
import { MdOutlineEdit } from "react-icons/md";

type TComponentType = React.ReactElement<{ config: TStoryContentElementTypeCustomEmbed }>;

type ExtendedDOMConversionMap<T extends HTMLElement = HTMLElement> = {
    div: (domNode: T) => {
        conversion: (domNode: T, component: TComponentType, editComponent: TComponentType) => null | DOMConversionOutput;
        priority: number;
    } | null
};

interface WrapperComponentProps {
    title: string;
    format: ElementFormatType | null;
    nodeKey: NodeKey;
    config: TStoryContentElementTypeCustomEmbed;
    component: TComponentType;
    editComponent: TComponentType;
}

function WrapperComponent(props: Readonly<WrapperComponentProps>) {
    const {
        title,
        format,
        nodeKey,
        config,
        component: Component,
        editComponent: EditComponent
    } = props;

    return (
        <BlockWithAlignableContents
            className={{
                base: 'EditorTheme__embedBlock',
                focus: 'EditorTheme__embedBlockFocus',
            }}
            format={format}
            nodeKey={nodeKey}
        >
            <EditableNodeWrapper
                title={title}
                nodeKey={nodeKey}
                Component={React.cloneElement(Component, { config })}
                EditComponent={React.cloneElement(EditComponent, { config })}
            />
        </BlockWithAlignableContents>
    );
}

export type SerializeEmbedNode = Spread<
    {
        config: TStoryContentElementTypeCustomEmbed
    },
    SerializedDecoratorBlockNode
>;

export class CustomEmbedNode extends DecoratorBlockNode {
    __title: string;
    __config: TStoryContentElementTypeCustomEmbed;
    __component: TComponentType;
    __editComponent: TComponentType;

    constructor(
        title: string,
        component: TComponentType,
        editComponent: TComponentType,
        config: TStoryContentElementTypeCustomEmbed,
        format?: ElementFormatType,
        key?: NodeKey,
    ) {
        super(format, key);
        this.__title = title;
        this.__config = config;
        this.__component = component;
        this.__editComponent = editComponent;
    }

    exportJSON(): SerializeEmbedNode {
        return {
            ...super.exportJSON(),
            config: this.__config,
        };
    }

    importDOM(): ExtendedDOMConversionMap | null {
        return {
            div: (domNode: HTMLElement) => {
                if (!domNode.hasAttribute('data-lexical-custom-embed-node')) {
                    return null;
                }

                return {
                    conversion: $convertNodeElement,
                    priority: 1,
                };
            },
        };
    }

    updateDOM(): false {
        return false;
    }

    getConfig(): TStoryContentElementTypeCustomEmbed {
        return this.__config;
    }

    setConfig(config: TStoryContentElementTypeCustomEmbed): void {
        const writable = this.getWritable();
        writable.__config = config;
    }

    getId(): string {
        return `${this.__config.subtype}-${this.__key}`;
    }

    decorate(): JSX.Element {
        return (
            <WrapperComponent
                title={this.__title || 'Custom Embed'}
                format={this.__format}
                nodeKey={this.getKey()}
                config={this.__config}
                component={this.__component}
                editComponent={this.__editComponent}
            />
        );
    }

    isInline(): false {
        return false;
    }

    isKeyboardSelectable(): true {
        return true;
    }
}

function $convertNodeElement(
    domNode: HTMLElement,
    component: TComponentType,
    editComponent: TComponentType,
): null | DOMConversionOutput {
    const configData = domNode.getAttribute('data-lexical-custom-embed-node');

    if (configData) {
        try {
            const config = JSON.parse(configData);
            const node = $createEmbedNode(component, editComponent, config);
            return { node };
        } catch (error) {
            console.error('Failed to parse custom embed node config:', error);
        }
    }

    return null;
}

export function $createEmbedNode(
    component: TComponentType,
    editComponent: TComponentType,
    config: TStoryContentElementTypeCustomEmbed,
    title: string = "Custom Embed",
): CustomEmbedNode {
    return new CustomEmbedNode(title, component, editComponent, config);
}

interface EditableNodeWrapperProps {
    title: string;
    nodeKey: NodeKey;
    wrapperClassName?: string;
    Component: React.ReactNode;
    EditComponent: React.ReactNode;
    dialogOptions?: {
        editComponentMaxWidth?: false | Breakpoint;
    }
}

export const EditableNodeWrapper: React.FC<EditableNodeWrapperProps> = (props) => {
    const { title, nodeKey, wrapperClassName = 'w-full', Component, EditComponent, dialogOptions } = props;

    const [editor] = useLexicalComposerContext();

    const handleDeleteNode = () => {
        editor.update(() => {
            const node = $getNodeByKey(nodeKey);

            if (node) {
                node.remove();
            }
        });
    }

    return (
        <div
            className={clsx("relative mx-auto h-full", wrapperClassName)}
        >
            <div
                className="z-50 bg-white p-1 shadow flex items-center gap-2 absolute top-2 right-2 cursor-pointer"
            >
                <SystemTooltip message="Delete Embed">
                    <FaRegTrashCan
                        onClick={handleDeleteNode}
                    />
                </SystemTooltip>

                {EditComponent && (
                    <ControlledSystemDialog
                        dialogProps={{
                            title,
                            maxWidth: dialogOptions?.editComponentMaxWidth
                        }}
                        trigger={{
                            component: (
                                <SystemTooltip message="Edit Embed">
                                    <MdOutlineEdit
                                        size={24}
                                    />
                                </SystemTooltip>
                            )
                        }}
                        content={{
                            component: (
                                EditComponent
                            )
                        }}
                        actionsProps={{
                            hide: true,
                        }}
                    />
                )}
            </div>

            {Component}
        </div>
    );
}